// ============================================================
// carousel-styles/presets.ts
// Trzy style karuzel Viral Partners
// ============================================================

import type { CarouselStyle, StyleColors } from "./types";

// --------------------------------------------------
// Brand constants (z brandbooka VP)
// --------------------------------------------------
export const VP_RED = "#E84C2B";
export const VP_BLACK = "#000000";
export const VP_WHITE = "#FFFFFF";
export const VP_GRAY = "#999999";

const baseColors: StyleColors = {
  accent: VP_RED,
  accentLight: "#FF6B4A",
  accentGlow: "#E84C2B22",
  accentBorder: "#E84C2B44",
  bg: "#0A0A0A",
  bgCard: "#111116",
  bgCardBorder: "#1a1a20",
  text: VP_WHITE,
  textMuted: "#888888",
  textSubtle: "#555555",
};

// --------------------------------------------------
// 1. NOIR EDITORIAL
// Magazynowy, typograficzny. Duży tekst, minimalne grafiki.
// --------------------------------------------------
export const noirEditorial: CarouselStyle = {
  id: "noir-editorial",
  name: "Noir Editorial",
  description:
    "Duża typografia jako główny element. Tekst robi robotę. Pewność siebie i autorytet.",
  bestFor: ["Thought leadership", "Case studies", "Brand building"],

  colors: {
    ...baseColors,
    bg: "#0A0A0A",
  },

  layout: {
    slidePadding: 36,
    aspectRatio: "1:1",
    safeZone: 60,
    topDeadZone: 150,
    borderRadius: 0,
  },

  cover: {
    titleSize: 38,
    titleWeight: 700,
    titleCase: "sentence",
    accentPosition: "last-line",
    showPageIndicator: true,
    showAccentBar: true,
    bgEffect: "gradient-subtle",
    subtitleSize: 13,
    subtitleColor: "subtle",
  },

  content: {
    titleSize: 26,
    bodySize: 14,
    bodyLineHeight: 1.7,
    numbering: "ghost-bg",
    numberSize: 72,
    showSeparator: true,
    showProgressDots: true,
    showProTipBox: false,
    metricBar: {
      enabled: false,
      borderRadius: 2,
      height: 3,
      colorGradient: [VP_RED],
    },
  },

  cta: {
    style: "circle-arrow",
    buttonRadius: 4,
    buttonVariant: "solid",
    buttonColors: [VP_RED, VP_RED],
    buttonTextSize: 13,
    buttonLetterSpacing: 1,
    showArrow: false,
  },
};

// --------------------------------------------------
// 2. DATA DRIVEN
// Duże cyfry, metryki, dashboard feel.
// --------------------------------------------------
export const dataDriven: CarouselStyle = {
  id: "data-driven",
  name: "Data Driven",
  description: "Duże cyfry, metryki, paski. Buduje autorytet przez dane. Idealny do case studies.",
  bestFor: ["Case studies", "Edukacja", "Social proof"],

  colors: {
    ...baseColors,
    bg: "#0D0D10",
    bgCard: "#111116",
    bgCardBorder: "#1a1a20",
  },

  layout: {
    slidePadding: 36,
    aspectRatio: "1:1",
    safeZone: 60,
    topDeadZone: 150,
    borderRadius: 0,
  },

  cover: {
    titleSize: 30,
    titleWeight: 700,
    titleCase: "sentence",
    accentPosition: "inline",
    showPageIndicator: false,
    showAccentBar: false,
    bgEffect: "dot-grid",
    subtitleSize: 11,
    subtitleColor: "subtle",
  },

  content: {
    titleSize: 16,
    bodySize: 12,
    bodyLineHeight: 1.5,
    numbering: "none",
    numberSize: 0,
    showSeparator: false,
    showProgressDots: false,
    showProTipBox: false,
    metricBar: {
      enabled: true,
      borderRadius: 2,
      height: 3,
      colorGradient: [VP_RED, "#D44025", "#B83520", "#9C2A1A"],
    },
  },

  cta: {
    style: "big-number",
    buttonRadius: 6,
    buttonVariant: "solid",
    buttonColors: [VP_RED, VP_RED],
    buttonTextSize: 13,
    buttonLetterSpacing: 1,
    showArrow: false,
  },
};

// --------------------------------------------------
// 3. BOLD CARDS
// Kartowy, modularny. Gradient accenty, badge numeracja.
// --------------------------------------------------
export const boldCards: CarouselStyle = {
  id: "bold-cards",
  name: "Bold Cards",
  description:
    "Karty z sekcjami, gradient accenty. Dynamiczny ale premium. Dobry do tipów i poradników.",
  bestFor: ["Tipy / poradniki", "Checklisty", "Edukacja"],

  colors: {
    ...baseColors,
    bg: "#08080A",
    bgCard: "#111116",
    bgCardBorder: "#1a1a22",
  },

  layout: {
    slidePadding: 28,
    aspectRatio: "1:1",
    safeZone: 60,
    topDeadZone: 150,
    borderRadius: 0,
  },

  cover: {
    titleSize: 34,
    titleWeight: 800,
    titleCase: "sentence",
    accentPosition: "last-line",
    showPageIndicator: false,
    showAccentBar: false,
    bgEffect: "glow",
    subtitleSize: 13,
    subtitleColor: "subtle",
  },

  content: {
    titleSize: 18,
    bodySize: 13,
    bodyLineHeight: 1.7,
    numbering: "badge-gradient",
    numberSize: 28,
    showSeparator: false,
    showProgressDots: false,
    showProTipBox: true,
    metricBar: {
      enabled: false,
      borderRadius: 2,
      height: 3,
      colorGradient: [VP_RED],
    },
  },

  cta: {
    style: "button-center",
    buttonRadius: 8,
    buttonVariant: "gradient",
    buttonColors: [VP_RED, "#FF6B4A"],
    buttonTextSize: 13,
    buttonLetterSpacing: 0,
    showArrow: false,
  },
};

// --------------------------------------------------
// Eksport wszystkich presetów
// --------------------------------------------------
export const VP_CAROUSEL_STYLES: CarouselStyle[] = [noirEditorial, dataDriven, boldCards];

/** Znajdź styl po ID */
export function getStyleById(id: string): CarouselStyle | undefined {
  return VP_CAROUSEL_STYLES.find((s) => s.id === id);
}
